import * as React from "react";
import { Grid } from "@mui/material";
import { useParams, useHistory } from "react-router-dom";
import { getHeaders } from "../utils/HeaderHelper";
import { AuthenticationContext } from "../Context";
import PendingActionWidget from "./PendingActionWidget";
import SearchToolsWidget from "./SearchToolsWidget";
import SearchResultsWidget from "./SearchResultsWidget";
import UnassignConfirmModal from "./UnassignConfirmModal";
import { useFlash } from "../FlashContext";

const ProgramUnassign: React.FC = (props: any) => {
  const { programId } = useParams();
  const history = useHistory();

  const authenticationSetting = React.useContext(AuthenticationContext);
  const { showToast } = useFlash();

  const [program, setProgram] = React.useState<any>(null);
  const [showConfirmUnassign, setShowConfirmUnassign] = React.useState(false);
  const [selectedPatients, setSelectedPatients] = React.useState<any[]>([]);

  const [paramsPresent, setParamsPresent] = React.useState<boolean>(true);
  const [searchPatients, setSearchPatients] = React.useState<any>([]);
  const [pendingAdd, setPendingAdd] = React.useState<any>([]);
  const [pendingRemoval, setPendingRemoval] = React.useState<any>([]);
  const [onList, setOnList] = React.useState<any>([]);

  const [loading, setLoading] = React.useState<boolean>(false);
  const [currentPage, setCurrentPage] = React.useState<number>(1);
  const [totalPages, setTotalPages] = React.useState<number>(1);
  const [sortObject, setSortObject] = React.useState<any>({
    field: "last_name",
    direction: "asc",
  });

  React.useEffect(() => {
    fetchProgram();
  }, [programId]);

  const fetchProgram = async () => {
    try {
      const response = await fetch(`/programs/${programId}/program_data`, {
        method: "GET",
        headers: getHeaders(authenticationSetting.csrfToken),
      });
      const result = await response.json();

      if (result.error) {
        showToast({ title: "Error", text: result.error, type: "error" });
      } else {
        setProgram(result?.program);
      }
    } catch (e) {
      console.error("Error fetching program", e);
      showToast({
        title: "Error",
        text: "Something went wrong fetching the program.",
        type: "error",
      });
    }
  };

  const openConfirm = () => {
    if (selectedPatients.length === 0) return;
    setShowConfirmUnassign(true);
  };

  const handleConfirmUnassign = async () => {
    try {
      const response = await fetch(`/programs/${programId}/unassign_patients`, {
        method: "POST",
        headers: getHeaders(authenticationSetting.csrfToken),
        body: JSON.stringify({
          patient_ids: selectedPatients.map((p) => p.id),
        }),
      });
      const result = await response.json();
      if (result?.success) {
        setShowConfirmUnassign(false);
        setSelectedPatients([]);
        setPendingAdd([]);
        setPendingRemoval([]);
        setOnList([]);
        showToast({
          title: "Patients Unassigned",
          text: "Patients unassigned successfully",
          type: "success",
        });
        history.push("/program-management");
      } else {
        showToast({
          title: "Unassign Failed",
          text: result?.message || "Failed to unassign patients",
          type: "error",
        });
      }
    } catch (e) {
      showToast({
        title: "Unassign Failed",
        text: `Unassign failed: ${e.message}`,
        type: "error",
      });
      console.error("Unassign failed:", e);
    }
  };

  return (
    <div className="patient-list-create-container">
      <div className="container2">
        <Grid item xs={12}>
          <Grid container>
            <Grid
              container
              justifyContent="space-between"
              className="floating-header"
            >
              <Grid item xs={6} className="list-create-header">
                <span
                  style={{
                    fontSize: "24px",
                    fontStyle: "normal",
                    fontWeight: 700,
                    lineHeight: "normal",
                    color: "black",
                  }}
                >
                  {`${program?.title || ""} - Unassign`}
                </span>
              </Grid>
              <PendingActionWidget
                selectedPatients={selectedPatients}
                pendingAdd={pendingAdd}
                pendingRemoval={pendingRemoval}
                onList={onList}
                handleCreate={openConfirm}
                mode="unassign"
              />
            </Grid>
            <div className="divider"></div>
            <Grid item xs={3}>
              <SearchToolsWidget
                csrfToken={props.csrfToken}
                fixedProgramId={Number(programId)}
                setSearchPatients={setSearchPatients}
                selectedPatients={selectedPatients}
                setSelectedPatients={setSelectedPatients}
                setOnList={setOnList}
                pendingAdd={pendingAdd}
                setParamsPresent={setParamsPresent}
                setLoading={setLoading}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                setTotalPages={setTotalPages}
                sortObject={sortObject}
              />
            </Grid>
            <Grid item xs={9}>
              <SearchResultsWidget
                searchPatients={searchPatients}
                setSearchPatients={setSearchPatients}
                selectedPatients={selectedPatients}
                setSelectedPatients={setSelectedPatients}
                setOnList={setOnList}
                setPendingAdd={setPendingAdd}
                setPendingRemoval={setPendingRemoval}
                onList={onList}
                pendingAdd={pendingAdd}
                pendingRemoval={pendingRemoval}
                paramsPresent={paramsPresent}
                loading={loading}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPages={totalPages}
                sortObject={sortObject}
                setSortObject={setSortObject}
              />
            </Grid>
          </Grid>
        </Grid>
      </div>

      {showConfirmUnassign && (
        <UnassignConfirmModal
          showConfirmUnassign={showConfirmUnassign}
          setShowConfirmUnassign={setShowConfirmUnassign}
          handleConfirmUnassign={handleConfirmUnassign}
          currentProgramName={program?.title || ""}
          patientsCount={selectedPatients.length?.toString()}
        />
      )}
    </div>
  );
};

export default ProgramUnassign;
